import { unwrap } from "@majourneys/api-client";
import { SUPPORTED_LANGS } from "@majourneys/shared";
import type { BlogDTO, Lang, LocalizedText } from "@majourneys/shared";
import { api } from "../../../lib/api";
import { latnLocale } from "../../../lib/format";
import { getI18n } from "../../../lib/i18n";
import type { BlogFilters } from "../constants/query-keys";

export interface LegacyBlogCategory {
  id: string;
  name: string;
  slug: string;
}

export interface LegacyBlog {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image?: string;
  date: string;
  /** ISO timestamp — kept alongside the formatted `date` for JSON-LD. */
  publishedAtIso?: string | null;
  category: LegacyBlogCategory;
  author: string;
  content?: string;
  galleryImages?: string[];
  hashtags?: string[];
  relatedPosts?: LegacyBlog[];
  /** Editor-supplied or computed read time in minutes. */
  readMinutes?: number | null;
  /** Pin to home blog hero. */
  featured?: boolean;
  /** Localized SEO description. */
  metaDescription?: string;
  /** Locales that have a real translation (en + non-empty content locales). */
  availableLocales?: Lang[];
}

export interface LegacyList {
  items: LegacyBlog[];
  page: number;
  totalPages: number;
  total: number;
}

function pickLocalized(text: LocalizedText | undefined | null, lang: Lang): string {
  if (!text) return "";
  return (text as Record<string, string | undefined>)[lang] || text.en || "";
}

/** "Articles", the generic blog label, in the page's language. */
function articlesLabel(lang: Lang): string {
  return getI18n(lang).t("blog.articles_label", { lng: lang });
}

/** The generic label (a missing category, or one literally named "Articles")
 *  is ours to translate; an editorial name is shown as written. */
function isGenericLabel(name: string | undefined): boolean {
  return !name || name.trim().toLowerCase() === "articles";
}

function firstHashtag(hashtags: string[] | undefined, lang: Lang): LegacyBlogCategory {
  const raw = hashtags?.[0] ?? "articles";
  const slug = raw.toLowerCase().replace(/\s+/g, "-");
  const name = isGenericLabel(raw) ? articlesLabel(lang) : raw.charAt(0).toUpperCase() + raw.slice(1);
  return { id: slug, name, slug };
}

/** Pick the joined `category` if present, else fall back to first hashtag. */
function resolveCategory(
  dto: BlogDTO,
  lang: Lang,
): LegacyBlogCategory {
  if (dto.category) {
    const own = (dto.category.name as Record<string, string | undefined> | undefined)?.[lang];
    const picked = own || pickLocalized(dto.category.name, lang);
    return {
      id: String(dto.category.id),
      name: own && !isGenericLabel(own) ? own : isGenericLabel(picked) ? articlesLabel(lang) : picked,
      slug: dto.category.slug ?? String(dto.category.id),
    };
  }
  return firstHashtag(dto.hashtags, lang);
}

function formatDate(iso: string | null | undefined, lang: Lang): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // The page's language, Latin digits in every locale (ar included), and
  // pinned to UTC so the server and the browser print the same day.
  return new Intl.DateTimeFormat(latnLocale(lang), {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(d);
}

function extractImagesFromHtml(html: string): string[] {
  if (!html) return [];
  const urls: string[] = [];
  const re = /<img[^>]+src=["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const src = m[1];
    if (!src) continue;
    if (src.startsWith("data:")) continue;
    urls.push(src);
  }
  return urls;
}

function mediaUrl(m: { spacesUrl: string; cfImageVariants: string[] | null }) {
  const variants = m.cfImageVariants ?? [];
  const publicVariant = variants.find((v) => v.endsWith("/public"));
  return publicVariant ?? variants[0] ?? m.spacesUrl;
}

function computeAvailableLocales(dto: BlogDTO): Lang[] {
  const content = dto.content as Record<string, string | undefined> | undefined;
  return (SUPPORTED_LANGS as readonly Lang[]).filter((l) => l === "en" || !!content?.[l]);
}

function mapBlogDto(dto: BlogDTO, lang: Lang): LegacyBlog {
  const content = pickLocalized(dto.content, lang);
  const contentImages = extractImagesFromHtml(content);
  const heroFromMedia = dto.heroMedia ? mediaUrl(dto.heroMedia) : undefined;
  const galleryFromMedia = (dto.galleryMedia ?? []).map(mediaUrl);
  // Priority: explicit URL → uploaded hero → first gallery → first body image.
  const image =
    dto.heroImageUrl ?? heroFromMedia ?? galleryFromMedia[0] ?? contentImages[0];
  const gallery = Array.from(
    new Set([
      ...(image ? [image] : []),
      ...galleryFromMedia,
      ...contentImages,
    ]),
  );
  // Use publishedAt when present so the date doesn't change every time the row
  // is touched in the admin.
  const dateIso = dto.publishedAt ?? dto.createdAt;
  return {
    id: String(dto.id),
    slug: dto.slug,
    title: pickLocalized(dto.title, lang),
    excerpt: pickLocalized(dto.shortDescription, lang),
    image,
    date: formatDate(dateIso, lang),
    publishedAtIso: dateIso,
    category: resolveCategory(dto, lang),
    author: "MaJourneys",
    content,
    galleryImages: gallery,
    hashtags: dto.hashtags ?? [],
    relatedPosts: [],
    readMinutes: dto.readMinutes,
    featured: dto.featured,
    metaDescription: pickLocalized(dto.metaDescription, lang),
    availableLocales: computeAvailableLocales(dto),
  };
}

/** Card-shaped projection used by every list response (blog index, sidebar,
 *  related-posts strip). Those surfaces read id/slug/title/excerpt/image/date/
 *  hashtags only — never `content` or `galleryImages`.
 *
 *  Keeping the body in was expensive in a non-obvious way: the article route
 *  prefetches a 50-post pool into the query cache, so TanStack Start dehydrated
 *  the full text of 50 articles into the SSR payload of every blog page. That
 *  was 546 KB of the 596 KB inline state script (687 KB HTML, 184 KB on the
 *  wire) for prose no reader ever sees. Derive the image first — it falls back
 *  to the first <img> in the body — then drop both heavy fields. */
function mapBlogCard(dto: BlogDTO, lang: Lang): LegacyBlog {
  const { content: _content, galleryImages: _galleryImages, ...card } =
    mapBlogDto(dto, lang);
  return card;
}

function filterByCategory(items: LegacyBlog[], category: string | undefined) {
  if (!category) return items;
  return items.filter((b) => b.category.slug === category);
}

function filterBySearch(items: LegacyBlog[], search: string | undefined) {
  if (!search) return items;
  const q = search.toLowerCase();
  return items.filter(
    (b) =>
      b.title.toLowerCase().includes(q) ||
      b.excerpt.toLowerCase().includes(q),
  );
}

/** Eden's typed client doesn't expose `/blogs/published` yet — issue a typed
 *  fetch via the underlying client by passing `publishedOnly=true` to `/blogs`.
 *  The API treats both routes the same internally. */
async function listPublished(query: Record<string, string>) {
  return unwrap(
    await api.blogs.get({ query: { ...query, publishedOnly: "true" } as Record<string, string> }),
  );
}

export const blogApi = {
  list: async (lang: Lang, filters?: BlogFilters): Promise<{ data: LegacyList }> => {
    const query: Record<string, string> = {};
    if (filters?.page !== undefined) query.page = String(filters.page);
    if (filters?.search) query.q = filters.search;
    const raw = await listPublished(query);

    const page = raw.page ?? 1;
    const pageSize = raw.pageSize ?? 20;
    const total = raw.total ?? raw.rows.length;
    let items = raw.rows.map((r: BlogDTO) => mapBlogCard(r, lang));
    items = filterByCategory(items, filters?.category);
    items = filterBySearch(items, filters?.search);

    return {
      data: { items, page, totalPages: Math.max(1, Math.ceil(total / pageSize)), total },
    };
  },

  getBySlug: async (slug: string, lang: Lang): Promise<{ data: LegacyBlog }> => {
    const dto = unwrap(await api.blogs.slug({ slug }).get());
    return { data: mapBlogDto(dto, lang) };
  },

  recentPosts: async (lang: Lang): Promise<{ data: LegacyBlog[] }> => {
    // Wider pool than the 4-5 posts the sidebar shows so related-post
    // clustering (buildRelatedPosts) has enough candidates to find same-city
    // / same-topic matches across the catalog, not just the newest handful.
    // pageSize must be one of ALLOWED_PAGE_SIZES ([10,20,50,100]) or the API
    // rejects the query with 400 "invalid pageSize" — 40 was invalid and broke
    // the recent/related section (and blocked the article from server-rendering).
    const raw = await listPublished({ page: "1", pageSize: "50" });
    return { data: raw.rows.map((r: BlogDTO) => mapBlogCard(r, lang)) };
  },
};

export const __test = { pickLocalized, computeAvailableLocales, mapBlogDto, mapBlogCard, formatDate, resolveCategory };
